import React, { createContext, ReactElement, ReactNode, useMemo } from "react";
import axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { log } from "../common/Logger";
import { AuthData } from "domain/TerminsucheTypes";
import { parseISO, parseJSON } from "date-fns";

interface Props {
  children: ReactNode;
}

interface ApiContextProps {
  api: AxiosInstance;
}

const axiosRequestConfiguration: AxiosRequestConfig = {
  baseURL: process.env.REACT_APP_API,
  withCredentials: true,
  responseType: "json",
  headers: {
    "Content-Type": "application/json",
  },
};

export const ApiContext = createContext<ApiContextProps>({
  api: axios.create(),
});

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(Z)?$/;

function isIsoDateString(value: unknown): boolean {
  return value !== undefined && typeof value === "string" && isoDateFormat.test(value);
}

export function handleDates<T>(body: T): T | void {
  if (body === null || body === undefined || typeof body !== "object") return body;

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (isIsoDateString(value)) body[key] = parseISO(value);
    else if (typeof value === "object") handleDates(value);
  }
}

export function ApiContextProvider({ children }: Props): ReactElement {
  //const authService = useContext(AuthContext);

  const api = useMemo(() => {
    const axiosInstance = axios.create(axiosRequestConfiguration);
    log.debug("initializing new axios api-instance");

    axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          log.debug("getting access token from auth-provider via api-context for request %s", config.url);
          let token = "";
          if (sessionStorage.Termindaten) {
            const termindaten: AuthData = JSON.parse(sessionStorage.Termindaten);
            token = termindaten.token;
          }
          config.headers.Authorization = `Bearer ${token}`;
          config.withCredentials = false;
          log.debug("added access token to request-header via api-context for request %s", config.url);
          return Promise.resolve(config);
        } catch (error) {
          log.error({ obj: error }, "oops during axios request interceptor for bearer tokens");
          return Promise.reject(error);
        }
      },
      (error) => {
        log.error("error while retrieving access token via api-context");
        Promise.reject(error);
      }
    );

    axiosInstance.interceptors.response.use((originalResponse) => {
      handleDates(originalResponse.data);
      return originalResponse;
    });

    // Trace-Parent für Logging mitgeben
    // TODO: sollte irgendwie pro "Aktion" generiert werden
    // axiosInstance.interceptors.request.use((value) => {
    //   value.headers.TraceParent = "12345-12345-12345";
    //   return value;
    // });

    return axiosInstance;
  }, []);

  return <ApiContext.Provider value={{ api }}>{children}</ApiContext.Provider>;
}
